import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';

// ── Glass tokens ─────────────────────────────────────────────────────────────
const G = {
  card: {
    background: 'rgba(5,20,40,0.72)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.18)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)',
  },
  strong: {
    background: 'rgba(3,54,111,0.82)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.2)',
  },
};

export default function DealsPage() {
  const navigate = useNavigate();
  const [deals, setDeals]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  useEffect(() => {
    fetch(`${API}/deals`)
      .then(r => r.json())
      .then(data => { setDeals(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(deals.map(d => d.providerCategory).filter(Boolean))];

  const filtered = deals.filter(d => {
    const matchSearch = !search ||
      d.title?.toLowerCase().includes(search.toLowerCase()) ||
      d.providerName?.toLowerCase().includes(search.toLowerCase()) ||
      d.description?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCategory === 'All' || d.providerCategory === filterCategory;
    return matchSearch && matchCat;
  });

  const getDealBadge = (deal) => {
    if (deal.discountType === 'percent') return `${deal.discountValue}% OFF`;
    if (deal.discountType === 'flat') return `$${deal.discountValue} OFF`;
    return 'DEAL';
  };

  const getDiscountedPrice = (deal) => {
    if (!deal.originalPrice) return null;
    if (deal.discountType === 'percent') return (deal.originalPrice * (1 - deal.discountValue / 100)).toFixed(2);
    if (deal.discountType === 'flat') return Math.max(0, deal.originalPrice - deal.discountValue).toFixed(2);
    return null;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const diff = new Date(expiryDate) - new Date();
    return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000;
  };

  const getDaysLeft = (expiryDate) => {
    if (!expiryDate) return null;
    const diff = new Date(expiryDate) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : null;
  };

  const avgSavings = Math.round(
    deals.filter(d => d.discountType === 'percent').reduce((s, d) => s + Number(d.discountValue), 0) /
    (deals.filter(d => d.discountType === 'percent').length || 1)
  );

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: 'DM Sans, sans-serif',
      backgroundImage: "url('https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=1800&q=85&auto=format&fit=crop')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    }}>
      {/* Dark overlay */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(3,10,25,0.55)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* ── NAV ── */}
        <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 2rem', height: 60, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)', borderBottom: '1px solid rgba(255,255,255,0.15)', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', fontWeight: 800, color: 'white', cursor: 'pointer', letterSpacing: '-0.3px' }} onClick={() => navigate('/')}>
            Vyb<span style={{ color: 'rgba(255,255,255,0.65)' }}>lo</span>
          </div>
          <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', borderRadius: '8px', padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
            ← Back
          </button>
        </nav>

        {/* ── HERO ── */}
        <div style={{ padding: '3.5rem 2rem 3rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, background: 'radial-gradient(circle, rgba(102,191,175,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 50, padding: '4px 14px', marginBottom: 16 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 6px #4ade80' }} />
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Limited Time Offers</span>
            </div>
            <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 5vw, 3rem)', fontFamily: 'Playfair Display, Georgia, serif', fontWeight: 800, margin: '0 0 12px', lineHeight: 1.1, textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>Today's Deals</h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16, margin: '0 0 32px' }}>Exclusive offers from top providers near you</p>

            {/* Search */}
            <div style={{ maxWidth: 500, margin: '0 auto', position: 'relative' }}>
              <span style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', fontSize: 16, pointerEvents: 'none' }}>🔍</span>
              <input
                type="text"
                placeholder="Search deals or providers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '14px 20px 14px 46px', borderRadius: 50, border: '1.5px solid rgba(255,255,255,0.25)', fontSize: 15, outline: 'none', boxSizing: 'border-box', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', color: 'white', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}
                onFocus={e => e.target.style.borderColor = 'rgba(255,255,255,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.25)'}
              />
            </div>

            {/* Stats row */}
            {!loading && deals.length > 0 && (
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 28, flexWrap: 'wrap' }}>
                {[
                  { label: 'Active Deals', value: deals.length },
                  { label: 'Providers', value: new Set(deals.map(d => d.providerId)).size },
                  { label: 'Avg Savings', value: `${avgSavings}%` },
                ].map((s, i) => (
                  <div key={i} style={{ ...G.card, borderRadius: 14, padding: '10px 22px', textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 800, color: 'white', lineHeight: 1, margin: 0 }}>{s.value}</p>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 0' }}>{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── CATEGORY TABS ── */}
        {categories.length > 1 && (
          <div style={{ padding: '0 2rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem', justifyContent: 'center' }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setFilterCategory(cat)}
                style={{ padding: '0.45rem 1.1rem', border: `1.5px solid ${filterCategory === cat ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.18)'}`, background: filterCategory === cat ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', color: filterCategory === cat ? 'white' : 'rgba(255,255,255,0.55)', borderRadius: 50, fontSize: '0.82rem', fontWeight: filterCategory === cat ? 700 : 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* ── MAIN CONTENT ── */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 60px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 80 }}>
              <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.15)', borderTop: '3px solid rgba(255,255,255,0.7)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading deals...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ ...G.card, borderRadius: 20, padding: '80px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🏷️</div>
              <h3 style={{ color: 'white', margin: '0 0 8px', fontFamily: 'Playfair Display, serif', fontSize: 22 }}>No deals found</h3>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 15 }}>Check back soon — providers post new deals regularly.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: 0 }}>
                  <strong style={{ color: 'white' }}>{filtered.length}</strong> deal{filtered.length !== 1 ? 's' : ''} available
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
                {filtered.map(deal => {
                  const discountedPrice = getDiscountedPrice(deal);
                  const expiringSoon = isExpiringSoon(deal.expiryDate);
                  const daysLeft = getDaysLeft(deal.expiryDate);
                  const savings = deal.discountType === 'percent' ? `Save ${deal.discountValue}%` : `Save $${deal.discountValue}`;

                  return (
                    <div key={deal.dealId}
                      style={{ ...G.card, borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'all 0.25s' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 48px rgba(0,0,0,0.45)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.3)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
                    >
                      {/* Deal banner — gradient glass */}
                      <div style={{ background: 'linear-gradient(135deg, rgba(3,54,111,0.95) 0%, rgba(3,139,148,0.9) 100%)', padding: '22px 20px 18px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <div style={{ background: '#f5c842', color: '#1a1a1a', padding: '4px 12px', borderRadius: 50, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em' }}>
                            {getDealBadge(deal)}
                          </div>
                          {expiringSoon && (
                            <div style={{ background: 'rgba(220,38,38,0.85)', color: 'white', padding: '4px 10px', borderRadius: 50, fontSize: 11, fontWeight: 700 }}>
                              ⏰ {daysLeft === 1 ? 'Last day!' : `${daysLeft}d left`}
                            </div>
                          )}
                        </div>
                        <h3 style={{ color: 'white', fontSize: 18, fontFamily: 'Playfair Display, Georgia, serif', fontWeight: 700, margin: '0 0 4px', lineHeight: 1.3, position: 'relative', zIndex: 1 }}>{deal.title}</h3>
                        {deal.serviceName && <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, margin: 0, position: 'relative', zIndex: 1 }}>{deal.serviceName}</p>}
                      </div>

                      {/* Card body */}
                      <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

                        {/* Provider */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          {deal.providerProfilePic ? (
                            <img src={deal.providerProfilePic} alt={deal.providerName} style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(3,54,111,0.6)', border: '2px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: 'white', flexShrink: 0 }}>
                              {deal.providerName?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'white' }}>{deal.providerName}</p>
                            {deal.providerCategory && <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{deal.providerCategory}</p>}
                          </div>
                        </div>

                        {/* Description */}
                        {deal.description && (
                          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.58)', lineHeight: 1.6, margin: 0 }}>{deal.description}</p>
                        )}

                        {/* Pricing block */}
                        {deal.originalPrice && (
                          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                              <span style={{ fontSize: 26, fontWeight: 800, color: '#7fffd4', fontFamily: 'Playfair Display, serif', lineHeight: 1 }}>${discountedPrice}</span>
                              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', textDecoration: 'line-through' }}>${deal.originalPrice}</span>
                            </div>
                            <span style={{ background: 'rgba(102,191,175,0.2)', color: '#66bfaf', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 50, border: '1px solid rgba(102,191,175,0.3)', whiteSpace: 'nowrap' }}>
                              {savings}
                            </span>
                          </div>
                        )}

                        {/* Expiry */}
                        {deal.expiryDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12 }}>{expiringSoon ? '⚠️' : '📅'}</span>
                            <span style={{ fontSize: 12, color: expiringSoon ? '#fca5a5' : 'rgba(255,255,255,0.35)', fontWeight: expiringSoon ? 700 : 400 }}>
                              Expires {new Date(deal.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        )}

                        {/* Book button */}
                        <button
                          onClick={() => navigate(`/provider/${deal.providerId}`)}
                          style={{ width: '100%', padding: '12px', background: 'rgba(3,54,111,0.8)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', marginTop: 'auto', fontFamily: 'DM Sans, sans-serif' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(3,54,111,1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(3,54,111,0.8)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}>
                          Book Now →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </div>
  );
}